<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import FileUpload from 'vue-upload-component';
import Button from 'dashboard/components-next/button/Button.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

// 20MB in bytes

const props = defineProps({
  ticketId: {
    type: [Number, String],
    required: true,
  },
});
const emit = defineEmits(['commentAdded']);
// Only allow images and videos
const ALLOWED_IMAGE_VIDEO_TYPES = 'image/*,video/*';
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_COMMENT_LENGTH = 1000;
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const newComment = ref('');
const loadingComment = ref(false);
const error = ref(null);
const attachedFiles = ref([]);
const fileUploadStates = ref(new Map()); // Track upload state per file

const hasAttachments = computed(() => attachedFiles.value.length > 0);

// Check if any file is currently uploading
const isUploadingFiles = computed(() => {
  if (fileUploadStates.value.size === 0) return false;
  return Array.from(fileUploadStates.value.values()).some(
    state => state === 'uploading'
  );
});

// Validate file type (image or video only)
const isValidFileType = file => {
  if (!file.type) return false;
  return file.type.startsWith('image/') || file.type.startsWith('video/');
};

// Validate file size (max 20MB)
const isValidFileSize = file => {
  return file.size <= MAX_FILE_SIZE;
};

// Set upload state for a file
const setFileUploadState = (fileId, state) => {
  fileUploadStates.value.set(fileId, state);
};

const addComment = async () => {
  if (!newComment.value.trim() && !hasAttachments.value) return;

  const ticketId = props.ticketId || route.params.id;

  if (!ticketId) {
    error.value = 'Ticket ID não encontrado';
    return;
  }

  try {
    loadingComment.value = true;
    error.value = null;

    const attachmentsPayload = attachedFiles.value.map(file => ({
      blob_signed_id: file.blobSignedId,
      filename:
        file.filename || file.resource?.file?.name || file.resource?.name,
      content_type:
        file.contentType || file.resource?.file?.type || file.resource?.type,
    }));

    await store.dispatch('tickets/addComment', {
      id: ticketId,
      content: newComment.value,
      attachments: attachmentsPayload,
    });

    // Clear the comment input and attachments
    newComment.value = '';
    attachedFiles.value = [];
    fileUploadStates.value.clear();

    // Refresh ticket history
    await store.dispatch('tickets/getTicketHistory', ticketId);

    emit('commentAdded');
  } catch (err) {
    error.value = err.message || 'Erro ao adicionar comentário';
  } finally {
    loadingComment.value = false;
  }
};

const onFileUpload = file => {
  if (!file) return;

  // Skip if file is being removed
  if (file.remove) {
    const fileId = file.id || file.resource?.id;
    if (fileId) {
      fileUploadStates.value.delete(fileId);
    }
    attachedFiles.value = attachedFiles.value.filter(
      f => f.resource?.id !== file.id
    );
    return;
  }

  const fileObj = file.file || file;
  if (!fileObj) return;

  // Validate file type
  if (!isValidFileType(fileObj)) {
    useAlert(t('TICKETS.UPLOAD.INVALID_FILE_TYPE'));
    return;
  }

  // Validate file size
  if (!isValidFileSize(fileObj)) {
    useAlert(t('TICKETS.UPLOAD.FILE_TOO_LARGE', { size: 20 }));
    return;
  }

  const fileId = file.id || `${fileObj.name}-${fileObj.size}-${Date.now()}`;

  // Set initial state and show start toast
  setFileUploadState(fileId, 'uploading');
  useAlert(t('TICKETS.UPLOAD.START', { filename: fileObj.name }));

  // Read file for preview
  const reader = new FileReader();
  reader.readAsDataURL(fileObj);

  reader.onloadend = async () => {
    // Use the account uploads endpoint since tickets don't have conversations
    if (!accountId.value) {
      setFileUploadState(fileId, 'error');
      error.value = 'Erro: Account ID não encontrado';
      useAlert(t('TICKETS.UPLOAD.ERROR'));
      return;
    }

    try {
      // Use the account upload endpoint (singular) which returns blob_id (signed_id)
      // The controller expects 'attachment' parameter
      const formData = new FormData();
      formData.append('attachment', fileObj);

      const axios = (await import('axios')).default;
      const response = await axios.post(
        `/api/v1/accounts/${accountId.value}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(currentUser.value?.access_token && {
              api_access_token: currentUser.value.access_token,
            }),
          },
        }
      );

      const blobSignedId = response.data?.blob_id;

      if (!blobSignedId) {
        setFileUploadState(fileId, 'error');
        error.value = 'Erro ao processar arquivo';
        useAlert(t('TICKETS.UPLOAD.ERROR'));
        return;
      }

      // Check if file already exists
      const existingIndex = attachedFiles.value.findIndex(
        f => f.resource?.id === file.id || f.resource?.name === fileObj.name
      );

      const newFile = {
        resource: file,
        isPrivate: false,
        thumb: reader.result,
        blobSignedId: blobSignedId,
        filename: fileObj.name || file.name,
        contentType: fileObj.type || file.type,
        fileId: fileId,
      };

      if (existingIndex >= 0) {
        // Update existing file
        attachedFiles.value[existingIndex] = newFile;
      } else {
        // Add new file
        attachedFiles.value = [...attachedFiles.value, newFile];
      }

      // Mark as uploaded and show success toast
      setFileUploadState(fileId, 'uploaded');
      useAlert(t('TICKETS.UPLOAD.SUCCESS', { filename: fileObj.name }));
    } catch (uploadError) {
      const errorMessage =
        uploadError?.response?.data?.error ||
        uploadError?.message ||
        'Erro ao fazer upload do arquivo';
      setFileUploadState(fileId, 'error');
      error.value = errorMessage;
      useAlert(t('TICKETS.UPLOAD.ERROR'));
    }
  };
};

const removeAttachment = updatedAttachments => {
  attachedFiles.value = updatedAttachments;
};
</script>

<template>
  <div class="p-6 border-b border-n-weak">
    <h2 class="text-lg font-semibold text-n-slate-12 mb-4">
      {{ t('TICKETS.ADD_COMMENT') }}
    </h2>
    <div class="flex space-x-3">
      <div class="flex-1">
        <TextArea
          v-model="newComment"
          show-character-count
          :max-length="MAX_COMMENT_LENGTH"
          :placeholder="t('TICKETS.ADD_COMMENT_PLACEHOLDER')"
          class="w-full"
        />
        <div v-if="hasAttachments" class="mt-2">
          <AttachmentPreview
            :attachments="attachedFiles"
            :upload-states="fileUploadStates"
            @remove-attachment="removeAttachment"
          />
        </div>
        <div class="flex items-center justify-between mt-2">
          <FileUpload
            :size="MAX_FILE_SIZE"
            :accept="ALLOWED_IMAGE_VIDEO_TYPES"
            multiple
            :drop-directory="false"
            :disabled="isUploadingFiles"
            @input-file="onFileUpload"
          >
            <Button
              v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
              icon="i-lucide-paperclip"
              slate
              faded
              sm
              :is-loading="isUploadingFiles"
              :disabled="isUploadingFiles"
            />
          </FileUpload>
        </div>
      </div>
      <Button
        :disabled="
          (!newComment.trim() && !hasAttachments) ||
          newComment.length > MAX_COMMENT_LENGTH ||
          loadingComment
        "
        slate
        size="sm"
        class="disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        @click="addComment"
      >
        {{ loadingComment ? t('TICKETS.SENDING') : t('TICKETS.COMMENT') }}
      </Button>
    </div>
  </div>
</template>
